import { useEffect } from 'react';

import { useTheme } from 'next-themes';

import { useAuthMe } from '@/features/auth/hooks/useAuthMe';
import { useAuthStore } from '@/store/authStore';
import { userPreferencesStorage } from '@/lib/storage/userPreferences';
import type { ThemeMode } from '@/types/userPreferences';

export function useThemePreference() {
  const { setTheme, resolvedTheme } = useTheme();
  const { data: user, isLoading } = useAuthMe();
  const accessToken = useAuthStore(state => state.accessToken);

  // Sync user's preference from localStorage on login
  useEffect(() => {
    if (isLoading) return;

    if (accessToken && user?.id) {
      // User is logged in - load their preference
      const preferences = userPreferencesStorage.get(user.id);

      // If the stored theme is different from current, update it
      // This ensures we switch to user's preference after login
      setTheme(preferences.theme);
    } else {
      // User is logged out - force light mode
      // This ensures login page is always light
      setTheme('light');
    }
  }, [accessToken, user?.id, isLoading, setTheme]);

  const updateTheme = (mode: ThemeMode) => {
    if (!user?.id) {
      // If not logged in, we can still update UI (e.g. for testing) but it won't persist to user pref
      setTheme(mode);
      return;
    }

    // Update localStorage for this user
    userPreferencesStorage.setTheme(user.id, mode);

    // Update UI immediately via next-themes
    setTheme(mode);
  };

  return {
    currentTheme: resolvedTheme as ThemeMode | undefined,
    updateTheme,
    isLoading,
  };
}
